import {
  calculateTotalCalories,
  calculateTotalCarbs,
  calculateTotalCo2e,
  calculateTotalFat,
  calculateTotalFiber,
  calculateTotalProtein,
  calculateTotalSugars,
} from '@sg/shared-fe-features-menu';

import { CostChannel } from '../../../../../../../graphql-schema/src/schema-types.generated';
import {
  useHomePickupMenuQuery,
  useHomeDeliveryMenuQuery,
  HomeMenuFragment,
} from './graphql/HomeMenu.generated';

export const useHomeMenu = (params: UseHomeMenuParams) => {
  const { restaurantId, addressId } = params;

  const [pickupMenuQueryResponse] = useHomePickupMenuQuery({
    pause: !restaurantId || Boolean(addressId),
    variables: {
      id: restaurantId || '',
      costChannel: CostChannel.PickupCostChannel,
    },
  });

  const [deliveryMenuQueryResponse] = useHomeDeliveryMenuQuery({
    pause: !addressId,
    variables: {
      input: { addressId },
      costChannel: CostChannel.DeliveryCostChannel,
    },
  });

  const isLoadingMenu = pickupMenuQueryResponse.fetching || deliveryMenuQueryResponse.fetching;

  const pickupMenuData = pickupMenuQueryResponse.data;
  const pickupMenu = pickupMenuData?.restaurant;

  const deliveryMenuData = deliveryMenuQueryResponse.data?.locationByAddress;
  const deliveryMenu = deliveryMenuData?.__typename === 'Location' ? deliveryMenuData.restaurant : undefined; // prettier-ignore

  const restaurant = addressId ? deliveryMenu : pickupMenu;
  const categories = restaurant?.menu?.categories;
  const products = categories?.flatMap((category) => category.products?.map(prepareProduct));

  return {
    restaurant,
    products,
    isLoadingMenu,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function prepareProduct(product: HomeProduct) {
  return {
    id: product.id,
    slug: product.slug,
    lineageKey: product.baseProduct?.lineageKey,
    isModifiable: product.isModifiable,
    title: product.name,
    description: product.description,
    cost: product.cost,
    image: product.asset?.url,
    allergenProperties: product.allergenProperties,
    calories: calculateTotalCalories(product.ingredients),
    totalFatG: calculateTotalFat(product.ingredients),
    totalCarbsG: calculateTotalCarbs(product.ingredients),
    dietaryFiberG: calculateTotalFiber(product.ingredients),
    sugarsG: calculateTotalSugars(product.ingredients),
    proteinG: calculateTotalProtein(product.ingredients),
    co2eKg: calculateTotalCo2e(product.ingredients),
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeProduct = HomeMenuFragment['menu']['categories'][number]['products'][number];

type UseHomeMenuParams = {
  restaurantId?: string;
  addressId?: string;
};
